/**
 * TagModal - Componente para CRUD de Tags
 * Modal reutilizável para criar e editar tags com estilo WhatsApp-like
 */

import React, { useState, useEffect } from 'react';
import { X, Tag, Hash, Palette, Type, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { TagsService, TagResponse, TagCreate, TagUpdate, TagUtils } from '../services/apiTags.ts';

interface TagModalProps {
  tag?: TagResponse;
  isOpen: boolean;
  onClose: () => void;
  onSave: (tag: TagResponse) => void;
  clinicId: number;
  existingCategories?: string[];
}

const TagModal: React.FC<TagModalProps> = ({
  tag,
  isOpen,
  onClose,
  onSave,
  clinicId,

  existingCategories = ['general', 'vip', 'paciente-novo', 'retorno', 'em-tratamento', 'inativo']
}) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState<TagCreate | TagUpdate>({
    name: '',
    category: 'general',
    color: TagUtils.generateRandomColor(),
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // Cores predefinidas inspiradas no WhatsApp
  const predefinedColors = [
    '#128C7E', // Verde WhatsApp
    '#075E54', // Verde escuro WhatsApp
    '#DCF8C6', // Verde claro WhatsApp
    '#34B7F1', // Azul WhatsApp
    '#4FC3F7', // Azul claro
    '#81C784', // Verde suave
    '#FFB74D', // Laranja suave
    '#F06292', // Rosa suave
    '#BA68C8', // Roxo suave
    '#9575CD', // Índigo suave
    '#4DD0E1', // Ciano suave
    '#AED581', // Lima suave
  ];

  // Resetar formulário quando o modal abrir/fechar
  useEffect(() => {
    if (isOpen) {
      if (tag) {
        // Modo edição
        setFormData({
          name: tag.name,
          category: tag.category || 'general',
          color: tag.color,
          description: tag.description || ''
        });
        setIsEdit(true);
      } else {
        // Modo criação
        setFormData({
          name: '',
          category: 'general',
          color: TagUtils.generateRandomColor(),
          description: ''
        });
        setIsEdit(false);
      }
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, tag]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length === 0) {
      setError('Nome da tag é obrigatório');
      return;
    }

    if (!TagUtils.isValidHexColor(formData.color || '')) {
      setError('Cor inválida. Use o formato #RRGGBB');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result: TagResponse;

      if (isEdit && tag) {
        // Atualizar tag existente
        result = await TagsService.updateTag(tag.id, formData as TagUpdate);
        console.log(`✅ Tag "${result.name}" atualizada com sucesso`);
      } else {
        // Criar nova tag
        result = await TagsService.createTag(clinicId, formData as TagCreate);
        console.log(`✅ Tag "${result.name}" criada com sucesso`);
      }

      setSuccess(true);
      onSave(result);

      // Fechar modal após 1 segundo
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao salvar tag';
      setError(errorMessage);
      console.error('❌ Erro ao salvar tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof (TagCreate | TagUpdate), value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColorSelect = (color: string) => {
    handleInputChange('color', color);
  };

  const previewStyle = {
    backgroundColor: formData.color || '#6B7280',
    color: TagUtils.getContrastColor(formData.color || '#6B7280')
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl shadow-2xl w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto transition-all ${isDark ? 'bg-gray-800' : 'bg-white'
        }`}>

        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
              }`}>
              <Tag className="w-5 h-5" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {isEdit ? 'Editar Tag' : 'Nova Tag'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors p-2 rounded-lg ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Preview */}
          <div className="flex justify-center">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
              style={previewStyle}
            >
              <Hash className="w-4 h-4" />
              <span>{formData.name || 'Nome da Tag'}</span>
            </div>
          </div>

          {/* Nome da Tag */}
          <div>
            <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <Type className="w-4 h-4" />
              Nome da Tag *
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-xl border outline-none transition-all ${isDark
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                }`}
              placeholder="Ex: Paciente VIP"
              maxLength={100}
              disabled={loading}
            />
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              {formData.name?.length || 0}/100 caracteres
            </div>
          </div>

          {/* Categoria */}
          <div>
            <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <Hash className="w-4 h-4" />
              Categoria
            </label>
            <select
              value={formData.category || 'general'}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-xl border outline-none transition-all appearance-none cursor-pointer ${isDark
                ? 'bg-gray-700 border-gray-600 text-white focus:border-sminy-primary'
                : 'bg-white border-slate-200 text-slate-900 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                }`}
              disabled={loading}
            >
              {existingCategories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
                </option>
              ))}
              <option value="custom">+ Personalizada...</option>
            </select>

            {formData.category === 'custom' && (
              <input
                type="text"
                value={formData.category === 'custom' ? '' : formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border outline-none transition-all mt-2 ${isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                  }`}
                placeholder="Nome da categoria personalizada"
                maxLength={50}
                disabled={loading}
              />
            )}
          </div>

          {/* Cor */}
          <div>
            <label className={`flex items-center gap-2 text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <Palette className="w-4 h-4" />
              Cor da Tag
            </label>

            <div className="grid grid-cols-6 gap-2 mb-3">
              {predefinedColors.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={`w-full h-10 rounded-lg border-2 transition-all hover:scale-110 ${formData.color === color
                    ? 'border-sminy-primary ring-2 ring-sminy-primary/30'
                    : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  style={{ backgroundColor: color }}
                  disabled={loading}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={formData.color || ''}
                onChange={(e) => handleInputChange('color', e.target.value)}
                className={`flex-1 px-4 py-2.5 rounded-xl border outline-none font-mono text-sm transition-all ${isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                  }`}
                placeholder="#128C7E"
                maxLength={7}
                disabled={loading}
              />
              <div
                className={`w-10 h-10 rounded-xl border-2 ${isDark ? 'border-gray-600' : 'border-slate-200'}`}
                style={{ backgroundColor: formData.color || '#6B7280' }}
              />
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className={`text-sm font-medium mb-2 block ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Descrição (opcional)
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-xl border outline-none transition-all resize-none ${isDark
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                }`}
              placeholder="Descreva o propósito desta tag..."
              rows={3}
              maxLength={500}
              disabled={loading}
            />
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              {formData.description?.length || 0}/500 caracteres
            </div>
          </div>

          {/* Erro */}
          {error && (
            <div className={`border rounded-lg p-3 text-sm ${isDark ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
              ⚠️ {error}
            </div>
          )}

          {/* Sucesso */}
          {success && (
            <div className={`border rounded-lg p-3 text-sm flex items-center gap-2 ${isDark ? 'bg-emerald-900/20 border-emerald-800 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600'}`}>
              <CheckCircle className="w-4 h-4" />
              Tag {isEdit ? 'atualizada' : 'criada'} com sucesso!
            </div>
          )}

        </form>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 p-6 border-t rounded-b-xl ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 transition-colors rounded-lg ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'}`}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || success}
            className="px-6 py-2 bg-sminy-primary text-white rounded-xl hover:bg-sminy-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm shadow-sminy-primary/20"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Salvando...
              </>
            ) : success ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Salvo!
              </>
            ) : (
              <>
                {isEdit ? 'Atualizar' : 'Criar'} Tag
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagModal;